import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Loader2, Paperclip, ArrowLeft } from "lucide-react";

export default function CreateTicket() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    subject: "",
    description: "",
    priority: "MEDIUM",
    customer_phone: "",
    site_location: "",
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/tickets", form);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          await api.post(`/tickets/${data.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (up) {
          toast.error("Ticket created but attachment failed: " + apiError(up.response?.data?.detail));
        }
      }
      qc.invalidateQueries();
      toast.success(`Ticket ${data.ticket_number} created`);
      navigate(`/tickets/${data.id}`);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="mb-6">
        <h1 className="font-heading font-black tracking-tighter text-4xl">New Ticket</h1>
        <p className="text-muted-foreground mt-1">Describe the product issue you're experiencing</p>
      </div>

      <form onSubmit={submit} className="max-w-2xl space-y-5 rounded-lg border border-border bg-card p-6" data-testid="ticket-form">
        {error && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">{error}</div>}

        <div className="space-y-2">
          <Label htmlFor="subject">Subject</Label>
          <Input id="subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required placeholder="e.g. Machine suddenly stops working" data-testid="ticket-subject" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required placeholder="Explain what happened, when it started, and any error codes…" data-testid="ticket-description" />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="customer_phone">Phone Number Customer</Label>
            <Input
              id="customer_phone"
              value={form.customer_phone}
              onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
              placeholder="e.g. +62 812-3456-789"
              data-testid="ticket-customer-phone"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="site_location">Site / Location</Label>
            <Input
              id="site_location"
              value={form.site_location}
              onChange={(e) => setForm({ ...form, site_location: e.target.value })}
              placeholder="e.g. Jakarta Plant A"
              data-testid="ticket-site-location"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Priority</Label>
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="LOW">Low</SelectItem>
              <SelectItem value="MEDIUM">Medium</SelectItem>
              <SelectItem value="HIGH">High</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="attachment" className="flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> Attachment (optional)</Label>
          <Input id="attachment" type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,.mp4,.mov" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ticket-attachment" />
          <p className="text-xs text-muted-foreground">Photo/video/PDF. Max 50MB.</p>
        </div>

        <Button type="submit" disabled={loading} data-testid="ticket-submit" className="gap-2">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Create Ticket
        </Button>
      </form>
    </Layout>
  );
}
